export function sortTimesheetsByPeriodDescending(timesheets = []) {
  return [...timesheets].sort((left, right) =>
    String(right.periodStart || '').localeCompare(String(left.periodStart || '')),
  );
}

export function pickCurrentTimesheet(timesheets = [], userId = null) {
  const rows = sortTimesheetsByPeriodDescending(timesheets);
  const visibleRows = userId ? rows.filter((row) => !row.userId || row.userId === userId) : rows;
  return (
    visibleRows.find((row) => row.status === 'draft' || row.status === 'resubmitted') ||
    visibleRows[0] ||
    null
  );
}

// Find the fetched timesheet whose weekly period actually covers `date`, restricted to the
// sheets visible to the user (mirroring pickCurrentTimesheet's visibility filter). This is the
// sheet the save path will write to for that date, so the log-time legend and the persisted
// destination stay in agreement regardless of which sheet pickCurrentTimesheet named as
// "current" (F-G04-001). Returns null when no fetched sheet covers the date, in which case the
// caller shows the auto-create message and anchors a new week on the entry date.
export function pickCoveringTimesheet(timesheets = [], date, userId = null) {
  const day = normalizeDateOnly(date);
  if (!day) return null;
  const rows = sortTimesheetsByPeriodDescending(timesheets);
  const visibleRows = userId ? rows.filter((row) => !row.userId || row.userId === userId) : rows;
  return visibleRows.find((row) => timesheetCoversDate(row, day)) || null;
}

// Calendar-day string (YYYY-MM-DD) for "today", using the same UTC slice convention the
// server's buildWeeklyPeriod relies on so an auto-created week always contains the anchor date.
export function todayDateOnly(now = new Date()) {
  return now.toISOString().slice(0, 10);
}

// Normalize any date-ish value to a YYYY-MM-DD calendar-day string. Empty/invalid -> ''.
export function normalizeDateOnly(value) {
  if (!value) return '';
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
}

// True when the entry date falls within a timesheet's weekly period (inclusive). Used by the
// create-then-continue spine to avoid attaching an entry to a stale draft from another week —
// which the server's in-period guard would otherwise reject. periods/date are YYYY-MM-DD strings.
export function timesheetCoversDate(timesheet, date) {
  if (!timesheet) return false;
  const day = normalizeDateOnly(date);
  const start = timesheet.periodStart;
  const end = timesheet.periodEnd;
  if (!day || !start || !end) return false;
  return day >= start && day <= end;
}

// Monday-based weekly period ({ periodStart, periodEnd }, YYYY-MM-DD) for any date-ish value,
// mirroring the server's buildWeeklyPeriod (UTC). Used to show which week a meeting-derived
// time draft lands in before it is created (AC-MTG-2.4 destination-week transparency).
export function weekPeriodForDate(value) {
  const day = normalizeDateOnly(value);
  if (!day) return { periodStart: null, periodEnd: null };
  const date = new Date(`${day}T00:00:00.000Z`);
  const weekday = date.getUTCDay();
  const distance = weekday === 0 ? -6 : 1 - weekday;
  date.setUTCDate(date.getUTCDate() + distance);
  const periodStart = date.toISOString().slice(0, 10);
  date.setUTCDate(date.getUTCDate() + 6);
  return { periodStart, periodEnd: date.toISOString().slice(0, 10) };
}

// Inclusive list of calendar days (YYYY-MM-DD) across a weekly period, for the grid's
// day picker. Defensive: invalid or reversed bounds return an empty list; spans are
// capped at 14 days so bad data cannot render an unbounded select.
export function enumerateWeekDays(periodStart, periodEnd) {
  const start = normalizeDateOnly(periodStart);
  const end = normalizeDateOnly(periodEnd);
  if (!start || !end || start > end) return [];
  const days = [];
  const cursor = new Date(`${start}T00:00:00.000Z`);
  for (let i = 0; i < 14; i += 1) {
    const day = cursor.toISOString().slice(0, 10);
    if (day > end) break;
    days.push(day);
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return days;
}

// Route to a time-entry target record so target cells render as linked names
// (AC-TIME-1.2). Category-style targets have no dedicated surface -> null.
export function timeTargetRoute(targetEntityType, targetEntityId) {
  if (!targetEntityId) return null;
  switch (String(targetEntityType || '')) {
    case 'project':
      return `/projects/${targetEntityId}`;
    case 'work_item':
      return `/work-items/${targetEntityId}`;
    case 'action':
      return `/actions/${targetEntityId}`;
    case 'meeting':
      return `/meetings/${targetEntityId}`;
    default:
      return null;
  }
}

// Ordered "This week" task-strip state. Pure so the Layer-2 checklist can be unit-tested.
// Steps: start -> log -> review -> submit. Each step is 'done' | 'active' | 'todo'.
// Scoped to the submitter's own workflow only (no approve/reject step — risk #6).
export function deriveThisWeekChecklist({ current = null, entryCount = 0 } = {}) {
  const hasWeek = Boolean(current);
  const status = String(current?.status || '').toLowerCase();
  const logged = Number(entryCount) > 0;
  const isOpen = status === 'draft' || status === 'resubmitted';
  const isClosed = status === 'submitted' || status === 'approved';

  return [
    {
      key: 'start',
      label: 'Start weekly timesheet',
      state: hasWeek ? 'done' : 'active',
      hint: hasWeek ? 'Current week created' : 'Create this week to begin',
    },
    {
      key: 'log',
      label: 'Log delivery time',
      state: !hasWeek ? 'todo' : logged ? 'done' : 'active',
      hint: !hasWeek ? 'Available after you start the week' : `${entryCount} entries captured`,
    },
    {
      key: 'review',
      label: 'Review entries',
      state: !hasWeek || !logged ? 'todo' : isClosed ? 'done' : 'active',
      hint: !hasWeek || !logged ? 'Available after you log time' : 'Check hours before submitting',
    },
    {
      key: 'submit',
      label: 'Submit for approval',
      state: isClosed ? 'done' : hasWeek && logged && isOpen ? 'active' : 'todo',
      hint: isClosed ? 'Submitted for approval' : 'Available after at least one entry',
    },
  ];
}
